import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Shield, ShieldOff, ShieldAlert, Users, Chrome, Calendar, ArrowUpCircle, ArrowDownCircle, Crown } from 'lucide-react';
import {
	Button, Card, CardContent, CardHeader, CardTitle,
	Badge, Textarea, useToast,
	Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@hiring-intelligence/ui';
import { usersApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';

export default function UsersPage() {
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const currentUser = useAuthStore((state) => state.user);
	const [blockDialog, setBlockDialog] = useState<{ open: boolean; userId: string; userName: string }>({ open: false, userId: '', userName: '' });
	const [blockReason, setBlockReason] = useState('');
	const [unblockDialog, setUnblockDialog] = useState<{ open: boolean; userId: string; userName: string }>({ open: false, userId: '', userName: '' });
	const [roleDialog, setRoleDialog] = useState<{ open: boolean; userId: string; userName: string; newRole: string }>({ open: false, userId: '', userName: '', newRole: '' });

	const { data, isLoading } = useQuery({
		queryKey: ['users'],
		queryFn: () => usersApi.list()
	});

	const blockMutation = useMutation({
		mutationFn: ({ id, reason }: { id: string; reason: string }) => usersApi.block(id, reason),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['users'] });
			toast({ title: 'User blocked', description: 'The user has been blocked successfully.' });
			setBlockDialog({ open: false, userId: '', userName: '' });
			setBlockReason('');
		},
		onError: (error: Error) => {
			toast({ title: 'Error', description: error.message, variant: 'destructive' });
		}
	});

	const unblockMutation = useMutation({
		mutationFn: (id: string) => usersApi.unblock(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['users'] });
			toast({ title: 'User unblocked', description: 'The user has been unblocked successfully.' });
			setUnblockDialog({ open: false, userId: '', userName: '' });
		},
		onError: (error: Error) => {
			toast({ title: 'Error', description: error.message, variant: 'destructive' });
		}
	});

	const roleMutation = useMutation({
		mutationFn: ({ id, role }: { id: string; role: string }) => usersApi.changeRole(id, role),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['users'] });
			toast({ title: 'Role updated', description: 'User role has been updated successfully.' });
			setRoleDialog({ open: false, userId: '', userName: '', newRole: '' });
		},
		onError: (error: Error) => {
			toast({ title: 'Error', description: error.message, variant: 'destructive' });
		}
	});

	const users = data?.data || [];

	return (
		<div className="p-4 md:p-6 space-y-6">
			<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
				<div>
					<h1 className="text-xl md:text-2xl font-bold">User Management</h1>
					<p className="text-sm text-muted-foreground">Manage talent acquisition users and their access</p>
				</div>
				<div className="flex items-center gap-2 text-sm text-muted-foreground">
					<Users className="h-4 w-4" />
					{users.length} user{users.length !== 1 ? 's' : ''}
				</div>
			</div>

			{isLoading ? (
				<div className="text-center py-12 text-muted-foreground">Loading users...</div>
			) : users.length === 0 ? (
				<Card>
					<CardContent className="py-12 text-center text-muted-foreground">
						No talent acquisition users found.
					</CardContent>
				</Card>
			) : (
				<div className="grid gap-4">
					{users.map((user: any) => (
						<Card key={user.id} className={user.isBlocked ? 'border-destructive/50 bg-destructive/5' : ''}>
							<CardHeader className="pb-3">
								<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
									<div className="flex items-center gap-3">
										<div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
											<span className="text-sm font-medium text-primary">
												{user.name?.charAt(0).toUpperCase()}
											</span>
										</div>
										<div className="min-w-0">
											<CardTitle className="text-base truncate">{user.name}</CardTitle>
											<p className="text-sm text-muted-foreground truncate">{user.email}</p>
											<div className="flex flex-wrap items-center gap-2 mt-1">
												{user.authProvider === 'google' && (
													<Badge variant="outline" className="gap-1 text-xs">
														<Chrome className="h-3 w-3" />
														Google SSO
													</Badge>
												)}
												<span className="flex items-center gap-1 text-xs text-muted-foreground">
													<Calendar className="h-3 w-3" />
													Joined {new Date(user.createdAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
												</span>
											</div>
										</div>
									</div>
									<div className="flex items-center gap-3 ml-13 sm:ml-0">
										{user.role === 'super_admin' ? (
											<Badge variant="secondary" className="gap-1 bg-purple-100 text-purple-800">
												<Crown className="h-3 w-3" />
												Super Admin
											</Badge>
										) : (
											<Badge variant="secondary" className="gap-1 bg-blue-100 text-blue-800">
												<Shield className="h-3 w-3" />
												Talent Acquisition
											</Badge>
										)}
										{user.isBlocked ? (
											<Badge variant="destructive" className="gap-1">
												<ShieldAlert className="h-3 w-3" />
												Inactive
											</Badge>
										) : (
											<Badge variant="secondary" className="gap-1 bg-green-100 text-green-800">
												<Shield className="h-3 w-3" />
												Active
											</Badge>
										)}
										<div className="flex items-center gap-1">
											{user.id !== currentUser?.id && !user.isBlocked && (
												user.role === 'talent_acquisition' ? (
													<Button
														variant="outline"
														size="sm"
														onClick={() => setRoleDialog({ open: true, userId: user.id, userName: user.name, newRole: 'super_admin' })}
														disabled={roleMutation.isPending}
													>
														<ArrowUpCircle className="mr-1 h-4 w-4" />
														<span className="hidden sm:inline">Promote</span>
													</Button>
												) : user.role === 'super_admin' ? (
													<Button
														variant="outline"
														size="sm"
														onClick={() => setRoleDialog({ open: true, userId: user.id, userName: user.name, newRole: 'talent_acquisition' })}
														disabled={roleMutation.isPending}
													>
														<ArrowDownCircle className="mr-1 h-4 w-4" />
														<span className="hidden sm:inline">Demote</span>
													</Button>
												) : null
											)}
											{user.role === 'talent_acquisition' && (
												user.isBlocked ? (
													<Button
														variant="outline"
														size="sm"
														onClick={() => setUnblockDialog({ open: true, userId: user.id, userName: user.name })}
														disabled={unblockMutation.isPending}
													>
														<ShieldOff className="mr-1 h-4 w-4" />
														<span className="hidden sm:inline">Activate</span>
													</Button>
												) : (
													<Button
														variant="destructive"
														size="sm"
														onClick={() => setBlockDialog({ open: true, userId: user.id, userName: user.name })}
														disabled={blockMutation.isPending}
													>
														<ShieldAlert className="mr-1 h-4 w-4" />
														<span className="hidden sm:inline">Deactivate</span>
													</Button>
												)
											)}
										</div>
									</div>
								</div>
							</CardHeader>
							{user.isBlocked && (
								<CardContent className="pt-0">
									<div className="rounded-md bg-destructive/10 p-3 text-sm">
										<p className="font-medium text-destructive">Reason:</p>
										<p className="text-muted-foreground mt-1">{user.blockedReason}</p>
										<div className="flex flex-wrap gap-4 mt-2 text-xs text-muted-foreground">
											{user.blockedAt && (
												<span>Deactivated: {new Date(user.blockedAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
											)}
											{user.blocker && (
												<span>By: {user.blocker.name}</span>
											)}
										</div>
									</div>
								</CardContent>
							)}
						</Card>
					))}
				</div>
			)}

			{/* Block User Dialog */}
			<Dialog open={blockDialog.open} onOpenChange={(open) => { if (!open) { setBlockDialog({ open: false, userId: '', userName: '' }); setBlockReason(''); } }}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Block User</DialogTitle>
						<DialogDescription>
							Are you sure you want to block <strong>{blockDialog.userName}</strong>? They will not be able to login until unblocked.
						</DialogDescription>
					</DialogHeader>
					<div className="py-2">
						<Textarea
							placeholder="Enter reason for blocking this user..."
							value={blockReason}
							onChange={(e) => setBlockReason(e.target.value)}
							rows={3}
						/>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => { setBlockDialog({ open: false, userId: '', userName: '' }); setBlockReason(''); }}>
							Cancel
						</Button>
						<Button
							variant="destructive"
							disabled={!blockReason.trim() || blockMutation.isPending}
							onClick={() => blockMutation.mutate({ id: blockDialog.userId, reason: blockReason })}
						>
							{blockMutation.isPending ? 'Blocking...' : 'Block User'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Unblock User Dialog */}
			<Dialog open={unblockDialog.open} onOpenChange={(open) => { if (!open) setUnblockDialog({ open: false, userId: '', userName: '' }); }}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Unblock User</DialogTitle>
						<DialogDescription>
							Are you sure you want to unblock <strong>{unblockDialog.userName}</strong>? They will be able to login again.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={() => setUnblockDialog({ open: false, userId: '', userName: '' })}>
							Cancel
						</Button>
						<Button
							disabled={unblockMutation.isPending}
							onClick={() => unblockMutation.mutate(unblockDialog.userId)}
						>
							{unblockMutation.isPending ? 'Unblocking...' : 'Unblock User'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Change Role Dialog */}
			<Dialog open={roleDialog.open} onOpenChange={(open) => { if (!open) setRoleDialog({ open: false, userId: '', userName: '', newRole: '' }); }}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{roleDialog.newRole === 'super_admin' ? 'Promote User' : 'Demote User'}</DialogTitle>
						<DialogDescription>
							{roleDialog.newRole === 'super_admin'
								? <>Are you sure you want to promote <strong>{roleDialog.userName}</strong> to <strong>Super Admin</strong>? They will have full system access.</>
								: <>Are you sure you want to demote <strong>{roleDialog.userName}</strong> to <strong>Talent Acquisition</strong>? They will lose admin privileges.</>
							}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={() => setRoleDialog({ open: false, userId: '', userName: '', newRole: '' })}>
							Cancel
						</Button>
						<Button
							disabled={roleMutation.isPending}
							onClick={() => roleMutation.mutate({ id: roleDialog.userId, role: roleDialog.newRole })}
						>
							{roleMutation.isPending ? 'Updating...' : roleDialog.newRole === 'super_admin' ? 'Promote' : 'Demote'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
